import { Action } from 'redux'
import { Logger } from '@west-australian-newspapers/typescript-log'
import { SWMAsyncThunkAction } from '../../store'

export interface SubscriptionOfferState {
    isInitializing: boolean
    initialized: boolean
    error?: string
    lastLoadedAt?: string
    lastPublicationId?: string
    lastOfferGroup?: string
    lastVariant?: string
}

const defaultState: SubscriptionOfferState = {
    isInitializing: false,
    initialized: false,
}

export const SUBSCRIPTION_OFFERS_INIT_REQUEST =
    '@@SUBSCRIPTION_OFFERS/INIT_REQUEST'
export const SUBSCRIPTION_OFFERS_INIT_SUCCESS =
    '@@SUBSCRIPTION_OFFERS/INIT_SUCCESS'
export const SUBSCRIPTION_OFFERS_INIT_FAILURE =
    '@@SUBSCRIPTION_OFFERS/INIT_FAILURE'

export interface SUBSCRIPTION_OFFERS_INIT_REQUEST_ACTION extends Action {
    type: typeof SUBSCRIPTION_OFFERS_INIT_REQUEST
    payload: {
        publicationId: string
        offerGroup?: string
        variant?: string
    }
}

export interface SUBSCRIPTION_OFFERS_INIT_SUCCESS_ACTION extends Action {
    type: typeof SUBSCRIPTION_OFFERS_INIT_SUCCESS
    payload: {
        publicationId: string
        offerGroup?: string
        variant?: string
        loadedAt: string
    }
}

export interface SUBSCRIPTION_OFFERS_INIT_FAILURE_ACTION extends Action {
    type: typeof SUBSCRIPTION_OFFERS_INIT_FAILURE
    payload: {
        publicationId: string
        offerGroup?: string
        variant?: string
        error: string
    }
}

export type SubscriptionOfferActions =
    | SUBSCRIPTION_OFFERS_INIT_REQUEST_ACTION
    | SUBSCRIPTION_OFFERS_INIT_SUCCESS_ACTION
    | SUBSCRIPTION_OFFERS_INIT_FAILURE_ACTION
    | { type: '@@INIT' }

export const subscriptionOfferReducer = (
    state: SubscriptionOfferState = defaultState,
    action: SubscriptionOfferActions,
): SubscriptionOfferState => {
    switch (action.type) {
        case SUBSCRIPTION_OFFERS_INIT_REQUEST: {
            return {
                ...state,
                isInitializing: true,
                error: undefined,
                lastPublicationId: action.payload.publicationId,
                lastOfferGroup: action.payload.offerGroup,
                lastVariant: action.payload.variant,
            }
        }
        case SUBSCRIPTION_OFFERS_INIT_SUCCESS: {
            return {
                ...state,
                isInitializing: false,
                initialized: true,
                error: undefined,
                lastPublicationId: action.payload.publicationId,
                lastOfferGroup: action.payload.offerGroup,
                lastVariant: action.payload.variant,
                lastLoadedAt: action.payload.loadedAt,
            }
        }
        case SUBSCRIPTION_OFFERS_INIT_FAILURE: {
            return {
                ...state,
                isInitializing: false,
                initialized: false,
                error: action.payload.error,
                lastPublicationId: action.payload.publicationId,
                lastOfferGroup: action.payload.offerGroup,
                lastVariant: action.payload.variant,
            }
        }
        default:
            return state
    }
}

export type InitSubscriptionOffersFn = (
    publicationId: string,
    log: Logger,
    options?: { offerGroup?: string; variant?: string },
) => Promise<any>

export function initSubscriptionOffersAsync(args: {
    publicationId: string
    log: Logger
    offerGroup?: string
    variant?: string
    initFn: InitSubscriptionOffersFn
}): SWMAsyncThunkAction<
    | SUBSCRIPTION_OFFERS_INIT_REQUEST_ACTION
    | SUBSCRIPTION_OFFERS_INIT_SUCCESS_ACTION
    | SUBSCRIPTION_OFFERS_INIT_FAILURE_ACTION
> {
    const { publicationId, log, offerGroup, variant, initFn } = args
    return async (dispatch) => {
        dispatch({
            type: SUBSCRIPTION_OFFERS_INIT_REQUEST,
            payload: { publicationId, offerGroup, variant },
        })

        try {
            await initFn(publicationId, log, { offerGroup, variant })

            dispatch({
                type: SUBSCRIPTION_OFFERS_INIT_SUCCESS,
                payload: {
                    publicationId,
                    offerGroup,
                    variant,
                    loadedAt: new Date().toISOString(),
                },
            })
        } catch (err: any) {
            const error =
                (err && (err.message || err.toString())) || 'Unknown error'

            log.error(
                { error, publicationId, offerGroup, variant },
                'Failed to init subscription offers',
            )

            dispatch({
                type: SUBSCRIPTION_OFFERS_INIT_FAILURE,
                payload: { publicationId, offerGroup, variant, error },
            })
        }
    }
}
